import { Fragment, useEffect, useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import { getProductList, type product } from '../../../utils/productUtils'
import ProductEntry from './ProductEntry'
import ListHeader from '../ListHeader'
import FilterBar, { type filter } from '../FilterBar'
import { getMessage } from '../../../utils/messageUtils'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import Spinner from '../../Spinner/Spinner'
import ProductPopUpContent from './ProductPopUpContent'

import './ProductList.scss'

interface ProductListProps {
  user: User
  isMobile: boolean
}

function ProductList({ user, isMobile }: ProductListProps) {
  const [loadData, setLoadData] = useState<boolean>(true)
  const [productList, setProductList] = useState<product[] | undefined>(
    undefined
  )
  const [shopTypes, setShopTypes] = useState<string[]>([])

  // Filters
  const [nameFilter, setNameFilter] = useState<string>('')
  const [shopFilter, setShopFilter] = useState<string>('')
  const [ingredientFilter, setIngredientFilter] = useState<boolean>(false)
  const [storableFilter, setStorableFilter] = useState<boolean>(false)
  const [packFilter, setPackFilter] = useState<boolean>(false)

  // Popup
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<(() => void) | undefined>(() => {})
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const generateShops = (products: product[] | undefined) => {
      if (!products) return
      const types = new Set<string>()
      products.forEach(product => {
        types.add(product.shop)
      })
      setShopTypes(Array.from(types))
    }

    const fetchProductList = async () => {
      setLoadData(false)
      setIsLoading(true)
      try {
        const data = await getProductList(user.id!)
        if (data.success) {
          generateShops(data.products)
          setProductList(data.products)
        }
      } finally {
        setIsLoading(false)
      }
    }
    if (loadData) {
      fetchProductList()
    }
  }, [loadData, user.id])

  const openPopup = (obj: object | null) => {
    const productToEdit = obj as product | null
    const title = productToEdit ? productToEdit.name : getMessage('product_new')
    setPopupMessage(title)
    setPopupChildren(
      <ProductPopUpContent
        product={productToEdit}
        currentShops={shopTypes}
        productList={productList}
        user={user}
        isMobile={isMobile}
        setPopupOnOk={setPopupOnOk}
        closePopup={() => setPopupOpen(false)}
        reLoadData={() => setLoadData(true)}
        setPopupExtraButtons={setPopupExtraButtons}
      />
    )
    setPopupOpen(true)
  }

  const applyFilters = (product: product) => {
    return (
      (nameFilter === '' ||
        product.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (ingredientFilter ? product.ingredient : true) &&
      (storableFilter ? product.storable : true) &&
      (packFilter ? product.pack > 1 : true)
    )
  }

  const generateProductList = () => {
    let previousShop = ''
    return productList
      ?.filter(product => applyFilters(product))
      .map(product => {
        if (product.shop !== previousShop) {
          previousShop = product.shop
          return (
            <Fragment>
              <ListHeader title={product.shop} isMobile={isMobile} />
              <ProductEntry
                product={product}
                isMobile={isMobile}
                openPopup={openPopup}
              />
            </Fragment>
          )
        }
        return (
          <ProductEntry
            product={product}
            isMobile={isMobile}
            openPopup={openPopup}
          />
        )
      })
  }

  const generateFilterItems = () => {
    return [
      {
        type: 'text',
        placeholder: getMessage('product'),
        value: nameFilter,
        setValue: setNameFilter,
      },
      {
        label: getMessage('shop'),
        type: 'select',
        options: shopTypes,
        value: shopFilter,
        setValue: setShopFilter,
      },
      {
        type: 'checkbox',
        checkboxes: [
          {
            label: getMessage('product_ingredient_filter'),
            value: ingredientFilter,
            setValue: setIngredientFilter,
          },
          {
            label: getMessage('product_storable_filter'),
            value: storableFilter,
            setValue: setStorableFilter,
          },
          {
            label: getMessage('product_packable_filter'),
            value: packFilter,
            setValue: setPackFilter,
          },
        ],
      },
    ] as filter[]
  }

  return (
    <div
      className={`product-list-container ${isMobile ? 'product-list-container-mobile' : ''}`}
    >
      <FilterBar
        filters={generateFilterItems()}
        openPopup={openPopup}
        isMobile={isMobile}
      />
      <div className="product-list-body-container">{generateProductList()}</div>
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        isMobile={isMobile}
        extraButtons={popupExtraButtons}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default ProductList
